import Link from 'next/link';
import { resolveHref } from './navigation.utils';
import { LinkLabel } from './LinkLabel';
import type { CtaItemDto, NavItemDto } from '@/lib/sanity/dto';
import type { ReactNode } from 'react';

type BaseLinkProps = {
    item: NavItemDto | CtaItemDto | null;
    className?: string;
    children?: ReactNode;
    ariaCurrent?: 'page' | 'true';
    scroll?: boolean;
    ariaHaspopup?: 'dialog';
    queryParams?: Record<string, string>;
    onClick?: () => void;
};

export function BaseLink({
    item,
    className,
    children,
    ariaCurrent,
    scroll,
    ariaHaspopup,
    queryParams,
    onClick,
}: BaseLinkProps) {
    if (!item) return null;

    const href = resolveHref(item, queryParams);
    const isExternal = item.linkType === 'external';
    const content = children ?? <LinkLabel item={item} />;

    if (isExternal) {
        // tel: and mailto: hand the href to another app — a new tab would leave
        // an empty one behind, so only web addresses get target/rel.
        const opensTab = /^https?:/i.test(href);

        return (
            <a
                href={href}
                className={className}
                target={opensTab ? '_blank' : undefined}
                rel={opensTab ? 'noopener noreferrer' : undefined}
                aria-current={ariaCurrent}
                onClick={onClick}
            >
                {content}
            </a>
        );
    }

    // A modal opens over the current page, so that page keeps its scroll position —
    // otherwise Next.js would scroll it to the top or to the anchor behind the dialog.
    const opensModal = Boolean(item.openInModal);

    return (
        <Link
            href={href}
            className={className}
            aria-current={ariaCurrent}
            aria-haspopup={ariaHaspopup ?? (opensModal ? 'dialog' : undefined)}
            scroll={scroll ?? (opensModal ? false : undefined)}
            onClick={onClick}
        >
            {content}
        </Link>
    );
}
